const stripTrailingSlash = (value: string) =>
  value !== '/' && value.endsWith('/') ? value.slice(0, -1) : value;

const ensureLeadingSlash = (value: string) =>
  value.startsWith('/') ? value : `/${value}`;

const ensureAbsolutePath = (value: string) => {
  if (!value) {
    return '/';
  }
  if (value === '/') {
    return '/';
  }
  return ensureLeadingSlash(value);
};

const getCurrentPathname = (): string => {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }

  return window.location.pathname || '';
};

const DEFAULT_BASE_PATH = '/waitlist';
const rawPublicUrl = process.env.PUBLIC_URL ?? '';

export const SITE_ORIGIN =
  process.env.REACT_APP_SITE_ORIGIN || 'https://rythm.fm';

const resolveBasePath = () => {
  if (!rawPublicUrl) {
    return DEFAULT_BASE_PATH;
  }

  try {
    const parsed = new URL(rawPublicUrl);
    return stripTrailingSlash(parsed.pathname) || '/';
  } catch {
    if (rawPublicUrl.startsWith('/')) {
      return stripTrailingSlash(rawPublicUrl) || '/';
    }
    return DEFAULT_BASE_PATH;
  }
};

export const BASE_PATH = resolveBasePath();

const hasConfiguredBasePath = () =>
  BASE_PATH !== '/' && BASE_PATH !== '';

const isBasePathActiveAtRuntime = (): boolean => {
  if (!hasConfiguredBasePath()) {
    return false;
  }

  const runtimePath = getCurrentPathname();
  if (!runtimePath) {
    // During prerender/server execution we default to the configured base path.
    return true;
  }

  return runtimePath.startsWith(BASE_PATH);
};

type BasePathOptions = {
  forceBase?: boolean;
  forceRoot?: boolean;
};

const shouldApplyBasePath = (options?: BasePathOptions): boolean => {
  if (!hasConfiguredBasePath()) {
    return false;
  }

  if (options?.forceBase) {
    return true;
  }

  if (options?.forceRoot) {
    return false;
  }

  return isBasePathActiveAtRuntime();
};

export const withBasePath = (path = '/', options?: BasePathOptions) => {
  const normalized = path === '/' ? '/' : ensureLeadingSlash(path);

  if (!shouldApplyBasePath(options)) {
    return normalized;
  }

  if (normalized === '/') {
    return BASE_PATH;
  }

  return `${BASE_PATH}${normalized}`;
};

export const withAssetPath = (path: string) => {
  const normalized = ensureLeadingSlash(path);

  if (rawPublicUrl) {
    const publicUrl = stripTrailingSlash(rawPublicUrl);
    return `${publicUrl}${normalized}`;
  }

  if (!hasConfiguredBasePath()) {
    return normalized;
  }

  return `${BASE_PATH}${normalized}`;
};

export const stripBasePath = (path: string) => {
  if (!path) {
    return path;
  }

  if (!hasConfiguredBasePath()) {
    return path;
  }

  if (path === BASE_PATH) {
    return '/';
  }

  if (path.startsWith(`${BASE_PATH}/`)) {
    return path.slice(BASE_PATH.length) || '/';
  }

  return path;
};

export const getRouterBasename = (): string | undefined => {
  if (shouldApplyBasePath()) {
    return BASE_PATH;
  }
  return undefined;
};

type CanonicalUrlOptions = {
  forceRoot?: boolean;
  forceBase?: boolean;
};

export const buildCanonicalUrl = (
  path = '/',
  options?: CanonicalUrlOptions,
) => {
  const targetPath = options?.forceRoot
    ? ensureAbsolutePath(path)
    : withBasePath(path, {
        forceBase: options?.forceBase,
        forceRoot: options?.forceRoot,
      });

  if (targetPath === '/') {
    return `${SITE_ORIGIN}/`;
  }

  return `${SITE_ORIGIN}${targetPath}`;
};
